import React from 'react';
import { BodyText, FlexColumn, FlexRow, Textarea } from '@make-software/cspr-design';
import styled from 'styled-components';
import {
  DropdownStyles,
  ErrorText,
  ErrorTextContainer,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import { SvgIcon } from '@make-software/cspr-design';
import DeleteBinIcon from 'assets/icons/ic-delete-bin.svg';
import { QuizQuestionUI } from 'app/components/management/common/plugins/quiz/types';
import Select from 'react-select';

const StyledQuestionBlock = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    gap: '4px',
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    border: '1px solid',
    padding: '8px',
    borderColor: theme.styleguideColors.borderPrimary,
    borderRadius: '12px'
  })
);

const StyledEmptyQuestionsBlock = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    minHeight: '92px',
    borderRadius: '12px',
    marginTop: '8px',
    marginBottom: '8px',
    backgroundColor: theme.styleguideColors.fillSecondary,
    alignItems: 'center',
    justifyContent: 'center'
  })
);

const StyledAddQuestionButton = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '48px',
    '&:hover': {
      color: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

const makeSlug = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[\s]+/g, '_') // spaces → _
    .replace(/[^a-z0-9_]+/g, '') // remove non-alphanum/underscore
    .replace(/_+/g, '_') // collapse multiple _
    .replace(/^_+|_+$/g, ''); // trim leading/trailing _

type QuizQuestionControlProps = {
  question: QuizQuestionUI;
  index: number;
  onChange: (next: QuizQuestionUI) => void;
  onRemove: () => void;
  errors?: any;
};

export const QuizQuestionControl = ({
  question,
  index,
  onChange,
  onRemove,
  errors
}: QuizQuestionControlProps) => {
  const update = (patch: Partial<QuizQuestionUI>) => {
    onChange({ ...question, ...patch });
  };

  const selectOptions = question.answers.map((ans) => ({
    value: ans,
    label: ans
  }));

  const onAnswersChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const rawLines = e.target.value.split('\n');

    // Keep all lines exactly as typed (no trimming, no filtering)
    const lines = rawLines;

    let correct = question.correctAnswer;
    if (correct && !lines.includes(correct)) {
      correct = '';
    }

    update({ answers: lines, correctAnswer: correct });
  };

  const onTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const title = e.target.value;
    const slug = makeSlug(title);
    update({ title, key: slug });
  };

  return (
    <StyledQuestionBlock>
      <FlexColumn style={{ flex: 4, gap: 8 }}>
        <StyledInput
          label={<BodyText size={1}>Question text</BodyText>}
          value={question.title}
          placeholder="What is this article about?"
          onChange={onTitleChange}
        />
        {errors?.title && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors.title.message}</ErrorText>
          </ErrorTextContainer>
        )}

        <Textarea
          label={<BodyText size={1}>Answers (one per line)</BodyText>}
          value={question.answers.join('\n')}
          placeholder={'Option 1\nOption 2\nOption 3\nOption 4'}
          onChange={onAnswersChange}
        />
        {errors?.answers && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors.answers.message}</ErrorText>
          </ErrorTextContainer>
        )}

        <FlexColumn>
          <BodyText size={1}>Correct answer</BodyText>
          <Select
            options={selectOptions}
            value={selectOptions.find((opt) => opt.value === question.correctAnswer) || null}
            styles={DropdownStyles()}
            onChange={(option) => {
              update({ correctAnswer: option?.value || '' });
            }}
          />
          {errors?.correctAnswer && (
            <ErrorTextContainer justify={'flex-start'}>
              <ErrorText size={3}>{errors.correctAnswer.message}</ErrorText>
            </ErrorTextContainer>
          )}
        </FlexColumn>
      </FlexColumn>

      {/* Remove button */}
      <FlexColumn style={{ flex: 1, width: '36px', alignItems: 'center', marginTop: 24 }}>
        <StyledDismissButton
          color={'utility'}
          height={'36'}
          onClick={onRemove}
          style={{ width: '36px' }}
        >
          <SvgIcon src={DeleteBinIcon} size={16} />
        </StyledDismissButton>
      </FlexColumn>
    </StyledQuestionBlock>
  );
};

// Re-export simple helpers to reuse in main component
export { StyledEmptyQuestionsBlock, StyledAddQuestionButton };
